import cn from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';
import { useExpEditor } from 'src/hooks/useExpEditor';

import Chevron from '../../../assets/icons/chevron.svg';

import styles from './JumpLinks.module.scss';

type JumpLink = {
  id: string;
  label: string;
};

const toSlug = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '');

const getOffset = (navRef: React.RefObject<HTMLElement | null>): number => {
  const jumpLinksHeight = navRef.current?.offsetHeight || 0;
  return jumpLinksHeight + 32;
};

const JumpLinks = (): JSX.Element => {
  const [links, setLinks] = useState<JumpLink[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const { t } = useDictionary();
  const { isEE } = useExpEditor();

  useEffect(() => {
    if (isEE) return;

    const collectLinks = () => {
      const offset = getOffset(navRef);
      const headings = document.querySelectorAll<HTMLHeadingElement>('h2.module-header');
      const jumpLinks: JumpLink[] = [];

      headings.forEach((heading) => {
        const label = heading.textContent?.trim() || '';
        if (!label) return;

        const id = heading.id || toSlug(label);
        if (!heading.id) heading.id = id;

        heading.style.scrollMarginTop = `${offset}px`;

        jumpLinks.push({ id, label });
      });

      setLinks(jumpLinks);
    };

    collectLinks();

    const observer = new MutationObserver(collectLinks);
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });

    return () => observer.disconnect();
  }, []);

  if (isEE || !links.length) return <></>;

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    setIsOpen(false);
    const target = document.getElementById(id);
    if (!target) return;
    scrollToTarget(target);
  };

  const scrollToTarget = (target: HTMLElement) => {
    const navHeight =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--NavigationHeader__height'),
        10
      ) || 0;
    let top = target.getBoundingClientRect().top + window.scrollY - getOffset(navRef);
    if (top < window.scrollY) {
      top = top - navHeight;
    }
    window.scrollTo({ top, behavior: 'smooth' });
  };

  return (
    <nav
      ref={navRef}
      className={cn(styles.main, 'container-12', 'spacer')}
      aria-label="Jump to section"
    >
      <button
        className={styles.toggle}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls="jump-links-list"
      >
        {t('CONTENTS')}
        <Chevron className={cn(styles.arrow, { [styles['arrow--active']]: isOpen })} />
      </button>
      <div
        id="jump-links-list"
        className={cn(styles['list-wrapper'], { [styles['active']]: isOpen })}
      >
        <ul className={styles.list}>
          {links.map(({ id, label }) => (
            <li key={id} className={styles.item}>
              <a href={`#${id}`} className={styles.link} onClick={(e) => handleLinkClick(e, id)}>
                {label}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
};

export default JumpLinks;
